import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BarChart3, Users, TrendingUp, CheckCircle2, AlertTriangle, Loader2, Target
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line,
} from "recharts";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.04 } } };
const item = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } };
const CHART_TOOLTIP = { borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12 };

const ManagerTeamAnalyticsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/manager/team-analytics").then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <AdminLayout title="Team Analytics" variant="manager">
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  const s = data?.summary || {};
  const members = data?.members || [];
  const trend = data?.monthly_trend || [];

  return (
    <AdminLayout title="Team Analytics" variant="manager">
      <motion.div className="space-y-6" variants={container} initial="hidden" animate="show" data-testid="team-analytics-page">
        <motion.div variants={item}>
          <h1 className="text-2xl font-extrabold tracking-tight">Team Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Performance metrics for your direct reports</p>
        </motion.div>

        {/* KPIs */}
        <motion.div variants={item} className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: "Team Size", value: s.team_size, icon: Users, color: "#3b82f6" },
            { label: "Enrollments", value: s.total_enrollments, icon: BarChart3, color: "#6366f1" },
            { label: "Completions", value: s.total_completions, icon: CheckCircle2, color: "#10b981" },
            { label: "Completion %", value: `${s.completion_rate}%`, icon: TrendingUp, color: s.completion_rate >= 70 ? "#10b981" : "#f59e0b" },
            { label: "Avg Score", value: `${s.avg_score}%`, icon: Target, color: "#8b5cf6" },
            { label: "Compliance", value: `${s.compliance_rate}%`, icon: CheckCircle2, color: s.compliance_rate >= 90 ? "#10b981" : "#ef4444" },
            { label: "Overdue", value: s.overdue_count, icon: AlertTriangle, color: s.overdue_count > 0 ? "#ef4444" : "#10b981" },
          ].map((st, i) => (
            <Card key={i} className="border shadow-none"><CardContent className="p-3.5">
              <div className="flex items-center gap-2 mb-1"><div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: st.color + "14" }}><st.icon className="w-3.5 h-3.5" style={{ color: st.color }} /></div></div>
              <p className="text-lg font-extrabold" style={{ color: st.color }}>{st.value}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">{st.label}</p>
            </CardContent></Card>
          ))}
        </motion.div>

        {/* Charts */}
        <motion.div variants={item} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border shadow-none">
            <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Team Member Completion Rates</CardTitle></CardHeader>
            <CardContent className="px-5 pb-5">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={members.slice(0, 10)} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} />
                  <Tooltip contentStyle={CHART_TOOLTIP} />
                  <Bar dataKey="completion_rate" name="Completion %" radius={[4, 4, 0, 0]} fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border shadow-none">
            <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Monthly Trend</CardTitle></CardHeader>
            <CardContent className="px-5 pb-5">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trend} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={CHART_TOOLTIP} />
                  <Line type="monotone" dataKey="enrollments" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} name="Enrollments" />
                  <Line type="monotone" dataKey="completions" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} name="Completions" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>

        {/* Member Table */}
        <motion.div variants={item}>
          <Card className="border shadow-none">
            <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Team Member Progress</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="grid grid-cols-12 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-5 py-2.5 border-b bg-muted/30">
                <span className="col-span-3">Name</span><span className="col-span-2 text-center">Enrolled</span>
                <span className="col-span-2 text-center">Completed</span><span className="col-span-2 text-center">Avg Score</span>
                <span className="col-span-3">Completion Rate</span>
              </div>
              {members.map((m, i) => (
                <div key={m.id} className="grid grid-cols-12 items-center px-5 py-3 border-b hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => navigate(`/manager/team/${m.id}`)} data-testid={`member-${i}`}>
                  <div className="col-span-3 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary shrink-0">{m.name?.[0]}</div>
                    <div className="min-w-0"><p className="text-sm font-medium truncate">{m.name}</p><p className="text-[10px] text-muted-foreground">{m.department}</p></div>
                  </div>
                  <span className="col-span-2 text-center text-sm">{m.enrolled}</span>
                  <span className="col-span-2 text-center text-sm font-bold text-green-600">{m.completed}</span>
                  <span className="col-span-2 text-center text-sm">{m.avg_score}%</span>
                  <span className="col-span-3 flex items-center gap-2">
                    <Progress value={m.completion_rate} className={cn("h-2 flex-1", m.completion_rate < 50 && "[&>div]:bg-red-500", m.completion_rate >= 50 && m.completion_rate < 80 && "[&>div]:bg-amber-500")} />
                    <span className={cn("text-xs font-bold w-10 text-right", m.completion_rate >= 80 ? "text-green-600" : m.completion_rate >= 50 ? "text-amber-600" : "text-red-600")}>{m.completion_rate}%</span>
                  </span>
                </div>
              ))}
              {members.length === 0 && <div className="text-center py-8 text-sm text-muted-foreground">No team members assigned</div>}
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AdminLayout>
  );
};

export default ManagerTeamAnalyticsPage;
